import { Injectable } from "@angular/core"
import { NodeFunctionBody, Function } from "@api/communication/models/functions"
import { ParentNode, ActiveNode, ArchivedNode, FlowNode } from "@api/communication/models/node"
@Injectable({
  providedIn: "root",
})
export class FlowNodeService {
  private draftid: string
  private rootid: string
  private name: string
  private objective: string
  private description: string
  private parentNode: ParentNode
  private chosenNode: FlowNode
  private duplicateCall: boolean
  private newNodePosition: number
  private editNodeMode: boolean
  private currentNodeList: FlowNode[]
  constructor() {}

  public setIds(draftid: string, rootid: string) {
    this.draftid = draftid
    this.rootid = rootid
  }
  public setCurrentNodeList(nodeList: FlowNode[]): void {
    this.currentNodeList = nodeList
  }
  public getCurrentNodeList(): FlowNode[] {
    return this.currentNodeList
  }
  getDraftId() {
    return this.draftid
  }
  getRootId() {
    return this.rootid
  }

  setNewNodePosition(newNodePosition: number) {
    this.newNodePosition = newNodePosition
  }
  getPositionNum(flowNode: FlowNode): number {
    var nodeLevel = flowNode.nodeLevel
    var position = flowNode.position
    var positionList = position.split("-")
    return positionList[nodeLevel] ? parseInt(positionList[nodeLevel]) : 0
  }
  getNewNodePosition(): number {
    return this.newNodePosition
  }

  public setAutomationInfo(name: string, objective: string, description: string) {
    this.name = name
    this.objective = objective
    this.description = description
  }

  public getAutomationName() {
    return this.name
  }

  public getAutomationObjective() {
    return this.objective
  }

  public getAutomationDescription() {
    return this.description
  }

  public setParentNode(parentNode: ParentNode) {
    this.parentNode = parentNode
  }

  public getParentNode() {
    return this.parentNode
  }

  public setChosenNode(chosenNode: FlowNode) {
    this.chosenNode = chosenNode
  }
  public getChosenNode(): FlowNode {
    return this.chosenNode
  }

  public getEditMode(): boolean {
    return this.editNodeMode
  }
  public setEditMode(editMode: boolean): void {
    this.editNodeMode = editMode
  }

  public setDuplicateCall(duplicateCall: boolean) {
    this.duplicateCall = duplicateCall
  }

  public getDuplicateCall() {
    return this.duplicateCall
  }

  public getLayerWidth(flowNodeArray: FlowNode[], currentFlowNode: FlowNode): number {
    var currentNodeLevel = currentFlowNode?.nodeLevel
    if (!currentNodeLevel) return 0
    var arrayLevelFiltered = flowNodeArray.filter((x) => x.nodeLevel > currentNodeLevel)
    let numberOfConditions: number = 0
    arrayLevelFiltered.forEach((node) => {
      if (node.nodeType === "FlowCondition") {
        numberOfConditions = numberOfConditions + 1
      }
    })
    return numberOfConditions
  }

  public distancePerConditionCurved(conditions: number): string {
    const baseValue = 256
    const increment = 422
    const distance = baseValue + increment * ((conditions - 1) / 2)
    return `${Math.round(distance)}px`
  }
  public isMultipleNodeType(nodeType: string): boolean {
    //TODO Add more multiple nodes (Splitter)
    return nodeType === "FlowCondition"
  }
  public emptyMultipleNode(node: FlowNode): boolean {
    let count = 0
    node.childNodes.forEach((id) => {
      if (id === "00000000-0000-0000-0000-000000000000") {
        count = count + 1
      }
    })
    return count === 2 ? true : false
  }

  public pushEmptyGuidsToArray(arr: string[]): string[] {
    while (arr.length < 2) {
      arr.push("00000000-0000-0000-0000-000000000000")
    }
    return arr
  }

  public collectNodeIds(nodeList: FlowNode[]): Set<string> {
    return new Set((nodeList ?? []).map((node) => node.id))
  }

  public danglingChildRefs(node: FlowNode, knownIds: Set<string>): string[] {
    return (node?.childNodes ?? []).filter((id) => id && id !== "00000000-0000-0000-0000-000000000000" && !knownIds.has(id))
  }

  public healChildRefs(node: FlowNode, knownIds: Set<string>): boolean {
    const dangling = this.danglingChildRefs(node, knownIds)
    if (dangling.length === 0) {
      return false
    }
    node.childNodes = node.childNodes.map((id) => (dangling.includes(id) ? "00000000-0000-0000-0000-000000000000" : id))
    return true
  }

  public nodesNotUsedThisLevel(level: number, nodeList: FlowNode[]): number {
    let parentLevel = level - 1
    let count = 0
    let usedCount = nodeList.filter((n) => n.nodeLevel === level).length
    var list = nodeList.filter((n) => n.nodeLevel === parentLevel)
    list.forEach((val) => {
      if (this.isMultipleNodeType(val.nodeType)) {
        count = count + 2
      } else {
        count = count + 1
      }
    })
    return count - usedCount
  }

  public doubleNodesCount(level: number, nodeList: FlowNode[]): number {
    var count = 0
    nodeList.forEach((x) => {
      if (this.isMultipleNodeType(x.nodeType) && x.nodeLevel === level) {
        count = count + 1
      }
    })
    return count
  }

  public doubleNodesCountTotal(): number {
    var count = 0
    this.currentNodeList.forEach((x) => {
      if (this.isMultipleNodeType(x.nodeType)) {
        count = count + 1
      }
    })
    return count
  }

  public getEventName(name: string): string {
    switch (name) {
      case "Reservation confirmed":
        return "ReservationConfirmed"
      case "Reservation cancelled":
        return "ReservationCancelled"
      case "Checked in":
        return "BookingCheckedin"
      case "Checked out":
        return "BookingCheckedout"
      case "AeroGuest check-in":
        return "GuestPreCheckedin"
      case "Signed registration card":
        return "RegistrationSigned"
      case "Paid for the stay":
        return "PaidForCheckin"
    }
  }

  public getArchivedNodeFromParentNode(parentNode: ParentNode): ArchivedNode {
    const archivedNode: ArchivedNode = {
      hotelId: parentNode.hotelId,
      rootId: parentNode.rootId,
      draftId: parentNode.draftId,
      eventName: parentNode.eventName,
      name: parentNode.name,
      description: parentNode.description,
      archivedSince: parentNode.archivedSince,
      lastEdited: parentNode.lastEdited,
      created: parentNode.created,
      status: parentNode.status,
      objective: parentNode.objective,
      createdBy: parentNode.createdBy,
      cronSchedule: parentNode.cronSchedule,
      startTime: parentNode.startTime,
      endTime: parentNode.endTime,
      lastEditedBy: parentNode.lastEditedBy,
      unPublishedChanges: parentNode.unPublishedChanges,
      activeEditing: parentNode.activeEditing,
    }
    return archivedNode
  }

  public createNewArchivedNodeFromParentNode(parentNode: ParentNode, user: User): ArchivedNode {
    const archivedNode: ArchivedNode = {
      hotelId: parentNode.hotelId,
      rootId: parentNode.rootId,
      draftId: parentNode.draftId,
      eventName: "",
      name: parentNode.name,
      description: parentNode.description,
      archivedSince: new Date(),
      lastEdited: new Date(),
      created: new Date(),
      status: "Draft",
      objective: parentNode.objective,
      createdBy: this.userDisplayName(user),
      lastEditedBy: this.userDisplayName(user),
      unPublishedChanges: parentNode.unPublishedChanges,
      activeEditing: parentNode.activeEditing,
    }
    return archivedNode
  }
  public createParentNodeFromArchivedNode(archivedNode: ArchivedNode): ParentNode {
    const parentNode: ParentNode = {
      createdBy: archivedNode?.createdBy,
      hotelId: archivedNode.hotelId,
      rootId: archivedNode.rootId,
      draftId: archivedNode.draftId,
      eventName: archivedNode.eventName,
      name: archivedNode.name,
      description: archivedNode.description,
      archivedSince: archivedNode.archivedSince,
      lastEdited: archivedNode.lastEdited,
      created: archivedNode.created,
      status: archivedNode.status,
      objective: archivedNode.objective,
      cronSchedule: archivedNode.cronSchedule,
      startTime: archivedNode.startTime,
      endTime: archivedNode.endTime,
      lastEditedBy: archivedNode.lastEditedBy,
      unPublishedChanges: archivedNode.unPublishedChanges,
      nodeType: "Archived",
      activeEditing: archivedNode.activeEditing,
    }
    return parentNode
  }

  public getActiveNodeFromParentNode(parentNode: ParentNode): ActiveNode {
    const activeNode: ActiveNode = {
      hotelId: parentNode.hotelId,
      rootId: parentNode.rootId,
      draftId: parentNode.draftId,
      eventName: parentNode.eventName,
      name: parentNode.name,
      description: parentNode.description,
      lastEdited: parentNode.lastEdited,
      created: parentNode.created,
      status: parentNode.status,
      objective: parentNode.objective,
      createdBy: parentNode.createdBy,
      cronSchedule: parentNode.cronSchedule,
      startTime: parentNode.startTime,
      endTime: parentNode.endTime,
      lastEditedBy: parentNode.lastEditedBy,
      unPublishedChanges: parentNode.unPublishedChanges,
      activeEditing: parentNode.activeEditing,
    }
    return activeNode
  }

  public createParentNodeFromActiveNode(activeNode: ActiveNode): ParentNode {
    const parentNode: ParentNode = {
      createdBy: activeNode.createdBy,
      hotelId: activeNode.hotelId,
      rootId: activeNode.rootId,
      draftId: activeNode.draftId,
      eventName: activeNode.eventName,
      name: activeNode.name,
      description: activeNode.description,
      lastEdited: activeNode.lastEdited,
      created: activeNode.created,
      status: activeNode.status,
      objective: activeNode.objective,
      cronSchedule: activeNode.cronSchedule,
      startTime: activeNode.startTime,
      endTime: activeNode.endTime,
      lastEditedBy: activeNode.lastEditedBy,
      unPublishedChanges: activeNode.unPublishedChanges,
      nodeType: "Active",
      activeEditing: activeNode.activeEditing,
    }
    return parentNode
  }

  getStaticVariable(bodyDataType: string, bodyData: any): NodeFunctionBody {
    if (bodyDataType === "Int64" && bodyData === null) {
      bodyDataType = "Nullable<Int64>"
    }
    const nodeFunctionBody: NodeFunctionBody = {
      ChildBodies: [],
      BodyDataType: bodyDataType,
      BodyType: "sv",
      BodyData: bodyData,
    }

    return nodeFunctionBody
  }

  getDynamicVariable(bodyData: string): NodeFunctionBody {
    const nodeFunctionBody: NodeFunctionBody = {
      ChildBodies: [],
      BodyDataType: "",
      BodyType: "dv",
      BodyData: bodyData,
    }

    return nodeFunctionBody
  }

  getNodeFunctionBody(ChildBodies: NodeFunctionBody[], BodyDataType: string, BodyType: string, BodyData: string) {
    const nodeFunctionBody: NodeFunctionBody = {
      ChildBodies: ChildBodies,
      BodyDataType: BodyDataType,
      BodyType: BodyType,
      BodyData: BodyData,
    }

    return nodeFunctionBody
  }

  getNodeFunction(hotelId: string, rootId: string, id: string, name: string, description: string, body: string) {
    const nodeFunction: Function = {
      hotelId: hotelId,
      rootId: rootId,
      id: id,
      name: name,
      description: description,
      _body: body,
    }

    return nodeFunction
  }

  getFlowNode(hotelId: string, rootId: string, id: string, name: string, description: string, nodeType: string, functionIds: string[], childNodes: string[], options: boolean[], nodeLevel: number) {
    const flowNode: FlowNode = {
      hotelId: hotelId,
      rootId: rootId,
      id: id,
      name: name,
      description: description,
      nodeType: nodeType,
      functionIds: functionIds ?? [],
      childNodes: childNodes ?? [],
      options: options ?? [],
      nodeLevel: nodeLevel,
    }
    return flowNode
  }

  updateArchivedFlowTimeAndBy(user: User): ArchivedNode {
    this.parentNode.lastEdited = new Date()
    this.parentNode.lastEditedBy = this.userDisplayName(user)
    this.parentNode.archivedSince = new Date()
    this.parentNode.unPublishedChanges = true
    this.setParentNode(this.parentNode)
    const archivedNode: ArchivedNode = this.getArchivedNodeFromParentNode(this.parentNode)
    return archivedNode
  }
  updateActiveFlowTimeAndBy(user: User): ActiveNode {
    this.parentNode.lastEdited = new Date()
    this.parentNode.lastEditedBy = this.userDisplayName(user)
    this.parentNode.archivedSince = new Date()
    this.parentNode.unPublishedChanges = true
    this.setParentNode(this.parentNode)
    const activeNode: ActiveNode = this.getActiveNodeFromParentNode(this.parentNode)
    return activeNode
  }

  public markFlowEdited(user: User): void {
    if (!this.parentNode) return
    this.parentNode.lastEdited = new Date()
    this.parentNode.lastEditedBy = this.userDisplayName(user)
    this.parentNode.unPublishedChanges = true
    localStorage.setItem("parentNode", JSON.stringify(this.parentNode))
  }

  public userDisplayName(user: User | null): string {
    return [user?.FirstName, user?.LastName].filter(Boolean).join(" ")
  }

  updateArchivedFlowNoUnpublished(user: User): ArchivedNode {
    this.parentNode.lastEdited = new Date()
    this.parentNode.lastEditedBy = this.userDisplayName(user)
    this.parentNode.archivedSince = new Date()
    this.parentNode.unPublishedChanges = false
    this.parentNode.rootId = this.parentNode.draftId
    this.setParentNode(this.parentNode)
    const archivedNode: ArchivedNode = this.getArchivedNodeFromParentNode(this.parentNode)

    return archivedNode
  }
  updateActiveFlowNoUnpublished(user: User): ActiveNode {
    this.parentNode.lastEdited = new Date()
    this.parentNode.lastEditedBy = this.userDisplayName(user)
    this.parentNode.archivedSince = new Date()
    this.parentNode.unPublishedChanges = false
    this.parentNode.rootId = this.parentNode.draftId
    this.setParentNode(this.parentNode)
    const activeNode: ActiveNode = this.getActiveNodeFromParentNode(this.parentNode)
    return activeNode
  }
  public setupFilterFunction(xandFunction: NodeFunctionBody, hotelid: string, draftId: string, functionid: string, name: string, description: string, body: string): Function {
    var templateFunc = this.getNodeFunction(hotelid, draftId, functionid, name, description, body)
    templateFunc._body = JSON.stringify(xandFunction)
    return templateFunc
  }
  public dateBetweenComparerFromOptions(offSetDays: number, functionType: string): string {
    if (offSetDays < 0 && functionType === "dv") {
      return "After"
    } else if (offSetDays > 0 && functionType === "f") {
      return "Before"
    }
  }

  public getBeforeOrAfterFromOptions(offsetDays: number, comparer: string): string {
    if (comparer === "<=") {
      return "Before"
    } else {
      return "After"
    }
  }
}
type User = {
  FirstName: string
  LastName: string
}
